import React, { useState, useEffect } from 'react';
import { BarChart3, Search, Zap, FileText, Download, RefreshCw, Lock, Eye, Users, Crown } from 'lucide-react';
import { useSearch } from '../utils/searchStorage';
import { useAuth } from '../utils/auth';

const UsageDashboard = () => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { search } = useSearch();
  const { getCurrentTier, checkUsageLimit, TIER_CONFIG } = useAuth();

  // Updated tier limits with privacy focus
  const tierLimits = {
    observer: { 
      searches: 10, 
      private_searches: 3, 
      api_calls: 50, 
      analyses: 5, 
      exports: 2 
    },
    professional: { 
      searches: -1, 
      private_searches: -1, 
      api_calls: 1000, 
      analyses: -1, 
      exports: -1 
    },
    business: { 
      searches: -1, 
      private_searches: -1, 
      api_calls: 10000, 
      analyses: -1, 
      exports: -1 
    },
    enterprise: { 
      searches: -1, 
      private_searches: -1, 
      api_calls: -1, 
      analyses: -1, 
      exports: -1 
    }
  };

  const fetchUsage = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get local storage stats first
      const localStats = search?.getPrivacyStats() || {};
      const currentTier = getCurrentTier();
      
      // Try to fetch from backend, fallback to local stats
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('http://127.0.0.1:8000/api/users/usage', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const backendData = await response.json();
          // Merge backend and local data
          setUsage({
            ...backendData,
            ...localStats,
            tier: currentTier,
            last_updated: new Date().toISOString()
          });
        } else {
          throw new Error('Backend unavailable');
        }
      } catch (backendError) {
        console.log('Backend unavailable, using local stats');
        // Use local storage stats
        setUsage({
          user_id: 'local',
          tier: currentTier,
          searches: localStats.totalSearches || 0,
          private_searches: localStats.privateSearches || 0,
          public_searches: localStats.publicSearches || 0,
          api_calls: 0, // No local tracking for API calls
          analyses: localStats.totalSearches || 0,
          exports: 0, // No local tracking for exports
          last_updated: new Date().toISOString(),
          source: 'local_storage'
        });
      }
      
    } catch (err) {
      setError('Unable to load usage data');
      console.error('Usage fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsage();
  }, [search, getCurrentTier]);

  const updateUsage = async (type, increment = 1) => {
    try {
      // For demo purposes - in real app this would hit your backend
      console.log(`Simulating ${type} usage +${increment}`);
      
      // Update local usage for demo
      setUsage(prev => ({
        ...prev,
        [type]: (prev[type] || 0) + increment,
        last_updated: new Date().toISOString()
      }));
      
    } catch (err) {
      console.error('Usage update error:', err);
    }
  };

  const getProgressPercentage = (current, limit) => {
    if (limit === -1) return 0; // unlimited
    return Math.min((current / limit) * 100, 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  const UsageCard = ({ icon: Icon, title, current, limit, type, color, description }) => {
    const percentage = getProgressPercentage(current, limit);
    const progressColor = getProgressColor(percentage);

    return (
      <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${color}`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white">{title}</h3>
              <p className="text-sm text-slate-400">
                {current} / {limit === -1 ? '∞' : limit}
              </p>
              {description && (
                <p className="text-xs text-slate-500 mt-1">{description}</p>
              )}
            </div>
          </div>
          <button
            onClick={() => updateUsage(type)}
            className="px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded-md text-sm font-medium text-slate-300 transition-colors"
          >
            Test +1
          </button>
        </div>
        
        {limit !== -1 && (
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${progressColor}`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        )}
        
        {limit !== -1 && percentage >= 90 && (
          <p className="text-xs text-red-400 mt-2 font-medium">
            ⚠️ Approaching limit
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-xl p-8">
        <div className="flex items-center justify-center space-x-2">
          <RefreshCw className="w-5 h-5 animate-spin text-blue-400" />
          <span className="text-slate-300">Loading usage data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-xl p-8">
        <div className="text-center">
          <div className="text-red-400 mb-2">⚠️ Error loading usage data</div>
          <p className="text-slate-400 mb-4">{error}</p>
          <button
            onClick={fetchUsage}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!usage) {
    return (
      <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-xl p-8">
        <div className="text-center text-slate-400">
          No usage data found. Start using the platform to see your stats!
        </div>
      </div>
    );
  }

  const userTier = usage.tier || 'observer';
  const limits = tierLimits[userTier] || tierLimits.observer;
  const tierConfig = TIER_CONFIG[userTier] || TIER_CONFIG.observer;

  // Get real-time privacy stats from search storage
  const privacyStats = search?.getPrivacyStats() || {};
  const canMakePrivate = search?.canMakePrivateSearch(userTier) || { canUse: false };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-6">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Usage Dashboard</h1>
              <p className="text-slate-400">
                Track your platform usage • Current Plan: 
                <span className="font-semibold capitalize text-blue-400 ml-1">
                  {tierConfig.name} {tierConfig.price > 0 && `($${tierConfig.price}/mo)`}
                </span>
              </p>
            </div>
            <button
              onClick={fetchUsage}
              className="flex items-center space-x-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="text-slate-300">Refresh</span>
            </button>
          </div>
        </div>

        {/* Privacy Focus Notice */}
        {userTier === 'observer' && (
          <div className="mb-6 bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-yellow-400" />
              <div>
                <h3 className="text-yellow-300 font-semibold">Privacy Limit Notice</h3>
                <p className="text-yellow-200 text-sm">
                  Free tier: {canMakePrivate.remaining || 0} private analyses remaining this month. 
                  <a href="/pricing" className="text-yellow-300 hover:underline ml-1">Upgrade for unlimited</a>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Usage Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <UsageCard
            icon={Search}
            title="Total Searches"
            current={usage.searches || 0}
            limit={limits.searches}
            type="searches"
            color="bg-blue-500"
            description="All website analyses"
          />
          
          <UsageCard
            icon={Lock}
            title="Private Searches"
            current={usage.private_searches || privacyStats.privateSearches || 0}
            limit={limits.private_searches}
            type="private_searches"
            color="bg-purple-500"
            description={userTier === 'observer' ? `${canMakePrivate.remaining || 0} remaining` : 'Unlimited privacy'}
          />
          
          <UsageCard
            icon={Eye}
            title="Public Searches"
            current={usage.public_searches || privacyStats.publicSearches || 0}
            limit={-1} // Always unlimited
            type="public_searches"
            color="bg-green-500"
            description="Shared with community"
          />
          
          <UsageCard
            icon={Zap}
            title="API Calls"
            current={usage.api_calls || 0}
            limit={limits.api_calls}
            type="api_calls"
            color="bg-orange-500"
            description={userTier === 'observer' ? 'Upgrade for API access' : 'Programmatic access'}
          />
        </div>

        {/* Privacy Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          
          {/* Privacy Breakdown */}
          <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Lock className="w-5 h-5 text-purple-400" />
              <h2 className="text-xl font-semibold text-white">Privacy Analytics</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Privacy Usage:</span>
                <span className="text-purple-300 font-medium">
                  {privacyStats.publicPercentage || 0}% Public
                </span>
              </div>
              
              <div className="w-full bg-slate-700 rounded-full h-3">
                <div 
                  className="h-3 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all duration-300"
                  style={{ width: `${privacyStats.publicPercentage || 0}%` }}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-slate-400">Public Analyses</div>
                  <div className="text-green-400 font-medium">{privacyStats.publicSearches || 0}</div>
                </div>
                <div>
                  <div className="text-slate-400">Private Analyses</div>
                  <div className="text-purple-400 font-medium">{privacyStats.privateSearches || 0}</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Tier Information */}
          <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Crown className="w-5 h-5 text-yellow-400" />
              <h2 className="text-xl font-semibold text-white">Plan Details</h2>
            </div>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Current Tier:</span>
                <span className="font-medium text-white capitalize">{tierConfig.name}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-slate-400">Monthly Cost:</span>
                <span className="font-medium text-white">
                  {tierConfig.price === 0 ? 'Free' : `$${tierConfig.price}`}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-slate-400">Privacy Level:</span>
                <span className="font-medium text-purple-300">
                  {userTier === 'enterprise' ? 'Private by Default' : 
                   userTier === 'observer' ? 'Limited (3/month)' : 'Full Control'}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-slate-400">Last Updated:</span>
                <span className="font-medium text-slate-300">
                  {usage.last_updated ? new Date(usage.last_updated).toLocaleString() : 'Never'}
                </span>
              </div>
              
              {usage.source === 'local_storage' && (
                <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-yellow-300 text-xs">
                  ℹ️ Using local storage data (backend unavailable)
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Upgrade CTA for Observer */}
        {userTier === 'observer' && (
          <div className="bg-gradient-to-r from-blue-900/40 to-purple-900/40 border border-blue-500/30 rounded-xl p-6 text-center">
            <h3 className="text-xl font-bold text-white mb-2">Ready for More?</h3>
            <p className="text-blue-200 mb-4">
              Upgrade to Professional for unlimited private analyses and advanced features
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a 
                href="/pricing" 
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all"
              >
                View Pricing
              </a>
              <a 
                href="/contact" 
                className="border border-slate-600 text-slate-300 px-6 py-3 rounded-lg font-semibold hover:bg-slate-800/50 transition-all"
              >
                Contact Sales
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// BillingSettings component
const BillingSettings = () => {
  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-white mb-8">Billing Settings</h1>
      <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6">
        <p className="text-slate-400">Billing settings component - coming soon!</p>
      </div>
    </div>
  );
};

export default UsageDashboard;
export { BillingSettings };